import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getAuthToken, setLoginToken, removeLoginToken, isImpersonating, clearAuth } from '../lib/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAuthToken());
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [currentStore, setCurrentStore] = useState(null);
  const [oauthLogin, setOauthLogin] = useState(false);

  useEffect(() => {
    if (token) {
      setStoresLoaded(false); // Reset until stores are fetched for the new token
      fetchUser();
    } else {
      setLoading(false);
      setStoresLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      fetchStores();
    }
  }, [user]);

  // Auto-refresh user data when tab becomes visible (picks up admin changes)
  useEffect(() => {
    if (!token) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      // Don't logout if this was an OAuth login - token was just set
      if (!oauthLogin) {
        logout();
      }
    } finally {
      setLoading(false);
      setOauthLogin(false);
    }
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(prev => {
        // Only update if something changed to avoid unnecessary re-renders
        if (JSON.stringify(prev) !== JSON.stringify(response.data)) {
          return response.data;
        }
        return prev;
      });
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
      if (response.data.length > 0) {
        const savedStoreId = localStorage.getItem('currentStoreId');
        const store = savedStoreId
          ? response.data.find(s => s.id === savedStoreId)
          : response.data[0];
        setCurrentStore(store || response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setStoresLoaded(true);
    }
  };

  const signup = async (userData) => {
    const response = await axios.post(`${API}/auth/signup`, userData);
    const data = response.data;
    if (data.requires_verification) return data;
    if (data.requires_2fa) return data;
    const { access_token, user } = data;
    setToken(access_token);
    setUser(user);
    setLoginToken(access_token);
    return data;
  };

  const login = async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    const data = response.data;
    if (data.requires_2fa) return data;
    const { access_token, user } = data;
    setToken(access_token);
    setUser(user);
    setLoginToken(access_token);
    return data;
  };

  const complete2FA = (data) => {
    const { access_token, user } = data;
    setToken(access_token);
    setUser(user);
    setLoginToken(access_token);
  };

  // ── Heartbeat for live session tracking ──────────────────
  const heartbeatRef = useRef(null);

  const sendHeartbeat = useCallback(async () => {
    if (!token) return;
    try {
      await axios.post(`${API}/auth/heartbeat`, {
        current_page: window.location.pathname,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language || navigator.userLanguage || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (token && user) {
      sendHeartbeat();
      heartbeatRef.current = setInterval(sendHeartbeat, 30000);
    }
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [token, user, sendHeartbeat]);

  const logout = async () => {
    // Record logout on server before clearing local state
    if (token) {
      try {
        await axios.post(`${API}/auth/logout-record`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* silent */ }
    }
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    setUser(null);
    setToken(null);
    setStores([]);
    setStoresLoaded(false);
    setCurrentStore(null);
    // Clear whichever scope this tab uses (sessionStorage for impersonation, localStorage otherwise)
    clearAuth();
    localStorage.removeItem('currentStoreId');
  };

  const switchStore = (store) => {
    setCurrentStore(store);
    localStorage.setItem('currentStoreId', store.id);
  };

  const value = {
    user,
    setUser,
    token,
    isImpersonating: isImpersonating(),
    setToken: (newToken) => {
      setToken(newToken);
      if (newToken) {
        setLoginToken(newToken);
      } else {
        removeLoginToken();
      }
    },
    setOauthLogin,
    loading,
    stores,
    storesLoaded,
    currentStore,
    signup,
    login,
    complete2FA,
    logout,
    switchStore,
    fetchStores,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};